import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

/**
 * 사용자에게 보이는 버전 문자열 — <b>{@code V2.0.<백엔드 이미지 번호>.<프런트 이미지 번호>}</b>.
 *
 * 뒤 두 자리는 사람이 올리는 숫자가 아니라 <b>Jenkins 가 찍은 이미지 태그</b>다
 * (`backend/Jenkinsfile`·`front/Jenkinsfile` 의 `${BUILD_NUMBER}`, 그대로 Docker 태그와
 * 매니페스트의 `image:` 로 들어간다). 그래서 화면의 숫자와 <b>클러스터에 떠 있는 이미지가 같은 값</b>이고,
 * "무슨 빌드를 보고 있느냐"를 물어볼 필요가 없어진다.
 *
 * 두 숫자가 나란히 가지 않는 것은 정상이다 — 잡이 따로 돌기 때문에 백엔드만 고치면 앞 숫자만 오른다.
 *
 * 앞의 {@link APP_BASE_VERSION} 은 제품 세대(v1/v2)라 <b>사람이 손으로</b> 올린다.
 */
export const APP_BASE_VERSION = 'V2.0';

/**
 * 프런트 이미지 번호. 빌드 시점에 번들로 굳는 상수다(`front/Dockerfile` 의 `BUILD_NUMBER` 빌드 인자).
 * 로컬 개발은 젠킨스가 없으므로 `dev`.
 */
export const FRONT_BUILD = import.meta.env.VITE_BUILD_NUMBER ?? 'dev';

type Meta = { service: string; version: string };

const metaQueryKey = ['meta'] as const;

/**
 * 백엔드 이미지 번호. 인증 없이 열려 있는 {@code /api/v1/meta} 가 내주는 값으로,
 * 로그인 화면에서도 읽힌다.
 *
 * 배포 중에는 파드가 갈리며 값이 바뀌므로 오래 캐시하지 않는다(5분). 실패하면 조용히 비운다 —
 * 버전 표시 하나 때문에 화면에 오류를 띄우지 않는다.
 */
export function useAppVersion(): string {
  const back = useBackendBuild();
  return versionLabel(back, FRONT_BUILD);
}

/** 백엔드 이미지 번호만. 못 읽었으면 {@code undefined} — 화면에 오류를 띄우지 않는다. */
export function useBackendBuild(): string | undefined {
  const meta = useQuery({
    queryKey: metaQueryKey,
    queryFn: () => apiFetch<Meta>('/v1/meta'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  return meta.data?.version;
}

/**
 * 두 번호를 사람이 읽는 한 줄로. 모르는 자리는 가운뎃점으로 남긴다 — 빈칸으로 두면
 * {@code V2.0..430} 처럼 <b>번호가 없는 것처럼</b> 보인다.
 */
export function versionLabel(backendBuild: string | undefined, frontBuild: string): string {
  return `${APP_BASE_VERSION}.${backendBuild ?? '·'}.${frontBuild}`;
}

/**
 * 관측된 배포 판 한 줄 (V361 · {@code GET /v1/releases}).
 *
 * <p>번호는 젠킨스가 빌드할 때 정해지므로 릴리즈 노트에 손으로 적을 수 없다. 그래서 떠 있는 판이
 * <b>스스로 남긴다</b> — 화면 진입 비콘이 프런트 번호를 싣고, 서버가 자기 번호와 짝지어 넣는다.
 */
export type BuildRelease = {
  /** V448 부터 실린다. 옛 서버가 떠 있는 동안은 없다. */
  id?: string;
  backendBuild: string;
  frontBuild: string;
  /** 그 짝이 <b>처음 보인</b> 시각. 배포 시각이 아니라 첫 사용자가 들어온 시각이다. */
  firstSeenAt: string;
};

/**
 * 관측된 판 목록(최신 먼저).
 *
 * <p>표가 V361 부터 쌓이므로 <b>그 전의 판은 없다</b>. 없는 것을 지어내지 않는다 — 옛 이력은
 * 날짜별 보기가 갖고 있다.
 */
export function useBuildReleases() {
  return useQuery({
    queryKey: ['releases'] as const,
    queryFn: () => apiFetch<BuildRelease[]>('/v1/releases'),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * 노트 한 줄이 처음 실려 나간 판 (V448 · {@code GET /v1/releases/notes}).
 *
 * <p>「Job 버전별」 의 정본이다 — 화면이 날짜로 짐작하지 않고 서버가 저장한 판을 읽는다.
 */
export type ReleaseNoteLink = {
  noteKey: string;
  /** 비어 있으면 「판 미지정」. */
  buildReleaseId: string | null;
  hidden: boolean;
};

export const releaseNoteLinksQueryKey = ['releases', 'notes'] as const;

export function useReleaseNoteLinks() {
  return useQuery({
    queryKey: releaseNoteLinksQueryKey,
    queryFn: () => apiFetch<ReleaseNoteLink[]>('/v1/releases/notes'),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** 「2026-09-30 14:20」. 초는 버린다 — 판을 가리키는 데 필요한 정밀도가 아니다. */
export function seenAtLabel(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;

  const two = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${two(at.getMonth() + 1)}-${two(at.getDate())} ${two(at.getHours())}:${two(at.getMinutes())}`;
}
